import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTimes } from 'react-icons/fa';

const GallerySection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-secondary);
`;

const GalleryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -10px;
    width: 60px;
    height: 3px;
    background-color: var(--color-accent);
    transform: translateX(-50%);
  }
`;

const SectionSubtitle = styled(motion.span)`
  font-size: 1rem;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: block;
`;

const GalleryGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  height: 300px;
  overflow: hidden;
  border-radius: 5px;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.6));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const GalleryCaption = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  color: var(--color-text);
  z-index: 2;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.3s ease;
  
  ${GalleryItem}:hover & {
    transform: translateY(0);
    opacity: 1;
  }
`;

const GalleryTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const GalleryDescription = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 900px;
  width: 100%;
  max-height: 80vh;
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: var(--color-text);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
`;

const ModalCaption = styled.div`
  position: absolute;
  bottom: -60px;
  left: 0;
  width: 100%;
  color: var(--color-text);
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const ModalDescription = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
`;

interface GalleryItemType {
  id: number;
  title: string;
  description: string;
  image: string;
}

const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryItemType | null>(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const galleryItems: GalleryItemType[] = [
    {
      id: 1,
      title: "Noivo Elegante",
      description: "Terno slim fit preto para noivos modernos",
      image: "/images/gallery-1.jpg"
    },
    {
      id: 2,
      title: "Smoking Premium",
      description: "Smoking azul royal com detalhes exclusivos",
      image: "/images/gallery-2.jpg"
    },
    {
      id: 3,
      title: "Padrinhos Coordenados",
      description: "Conjunto de ternos para padrinhos em tons coordenados",
      image: "/images/gallery-3.jpg"
    },
    {
      id: 4,
      title: "Casamento na Praia",
      description: "Terno leve em linho para casamentos ao ar livre",
      image: "/images/gallery-4.jpg"
    },
    {
      id: 5,
      title: "Formatura com Estilo",
      description: "Terno cinza para formaturas e eventos acadêmicos",
      image: "/images/gallery-5.jpg"
    },
    {
      id: 6,
      title: "Evento Black Tie",
      description: "Smoking clássico para eventos formais",
      image: "/images/gallery-6.jpg"
    },
    {
      id: 7,
      title: "Terno Azul Marinho",
      description: "Opção versátil para diversas ocasiões",
      image: "/images/gallery-7.jpg"
    },
    {
      id: 8,
      title: "Detalhe em Colete",
      description: "Conjunto com colete para um visual diferenciado",
      image: "/images/gallery-8.jpg"
    }
  ];
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };
  
  const openModal = (item: GalleryItemType) => {
    setSelectedImage(item);
  };
  
  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <GallerySection id="gallery" ref={ref}>
      <GalleryContainer>
        <SectionHeader>
          <SectionSubtitle
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            Nossa Galeria
          </SectionSubtitle>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Inspirações e Clientes
          </SectionTitle>
        </SectionHeader>
        
        <GalleryGrid
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {galleryItems.map((item) => (
            <GalleryItem
              key={item.id}
              variants={itemVariants}
              onClick={() => openModal(item)}
              whileHover={{ scale: 1.03 }}
            >
              <GalleryImage src={item.image} alt={item.title} />
              <GalleryCaption>
                <GalleryTitle>{item.title}</GalleryTitle>
                <GalleryDescription>{item.description}</GalleryDescription>
              </GalleryCaption>
            </GalleryItem>
          ))}
        </GalleryGrid>
      </GalleryContainer>
      
      <AnimatePresence>
        {selectedImage && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
              <ModalImage src={selectedImage.image} alt={selectedImage.title} />
              <ModalCaption>
                <ModalTitle>{selectedImage.title}</ModalTitle>
                <ModalDescription>{selectedImage.description}</ModalDescription>
              </ModalCaption>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </GallerySection>
  );
};

export default Gallery; 